import Image from "next/image";

import { Flag } from "./flag";

export type CourseIconName =
  "linkedin" | "excel" | "figma" | "code" | "briefcase" | "graduation" | "book";

const SUPPORTED_FLAGS = new Set<string>(["en", "es", "fr", "de", "it"]);

interface CourseIconProps {
  language?: string | null;
  icon?: string | null;
  size?: number;
  className?: string;
}

export function CourseIcon({
  language,
  icon,
  size = 40,
  className = "",
}: CourseIconProps) {
  if (language && SUPPORTED_FLAGS.has(language)) {
    return (
      <div
        className={`overflow-hidden rounded-lg ${className}`}
        style={{ width: size, height: size }}
      >
        <Flag code={language as "en" | "es" | "fr" | "de" | "it"} />
      </div>
    );
  }

  if (icon?.startsWith("http")) {
    return (
      <Image
        src={icon}
        alt=""
        width={size}
        height={size}
        className={className}
        aria-hidden="true"
        style={{ objectFit: "contain" }}
      />
    );
  }

  const iconName = (icon || "book") as CourseIconName;
  const svgContent = brandIcons[iconName] || brandIcons.book;

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      fill="none"
      className={className}
      aria-hidden="true"
    >
      {svgContent}
    </svg>
  );
}

const brandIcons: Record<string, React.ReactNode> = {
  linkedin: (
    <>
      <rect x="4" y="4" width="56" height="56" rx="12" fill="#0A66C2" />
      <path
        d="M18 27h6v19h-6V27Zm3-9a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7ZM28 27h5.7v2.6h.1c.8-1.5 2.7-3.1 5.6-3.1 6 0 7.1 3.9 7.1 9.1V46h-6V36.7c0-2.2 0-5-3.1-5s-3.5 2.4-3.5 4.9V46H28V27Z"
        fill="white"
      />
    </>
  ),
  excel: (
    <>
      <rect x="4" y="4" width="56" height="56" rx="8" fill="#217346" />
      <path
        d="M16 16h14v8H16v-8Zm18 0h14v8H34v-8ZM16 28h14v8H16v-8Zm18 0h14v8H34v-8ZM16 40h14v8H16v-8Zm18 0h14v8H34v-8Z"
        fill="white"
        opacity="0.3"
      />
      <text
        x="32"
        y="40"
        textAnchor="middle"
        fill="white"
        fontSize="20"
        fontWeight="bold"
        fontFamily="sans-serif"
      >
        X
      </text>
    </>
  ),
  figma: (
    <>
      <circle cx="32" cy="32" r="28" fill="#1E1E1E" />
      <circle cx="32" cy="22" r="7" fill="#FF7262" />
      <circle cx="22" cy="22" r="7" fill="#F24E1E" />
      <circle cx="22" cy="32" r="7" fill="#A259FF" />
      <circle cx="22" cy="42" r="7" fill="#0ACF83" />
      <circle cx="32" cy="32" r="7" fill="#1ABCFE" />
    </>
  ),
  code: (
    <>
      <rect x="4" y="4" width="56" height="56" rx="12" fill="#1E293B" />
      <path
        d="M22 24l-8 8 8 8"
        stroke="#38BDF8"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M42 24l8 8-8 8"
        stroke="#38BDF8"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M36 18l-8 28"
        stroke="#94A3B8"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </>
  ),
  briefcase: (
    <>
      <rect x="4" y="4" width="56" height="56" rx="12" fill="#7C3AED" />
      <rect
        x="14"
        y="22"
        width="36"
        height="24"
        rx="4"
        stroke="white"
        strokeWidth="2.5"
      />
      <path
        d="M24 22V18a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v4"
        stroke="white"
        strokeWidth="2.5"
      />
      <path d="M14 32h36" stroke="white" strokeWidth="2" />
      <rect
        x="28"
        y="28"
        width="8"
        height="8"
        rx="2"
        fill="white"
        opacity="0.3"
      />
    </>
  ),
  graduation: (
    <>
      <rect x="4" y="4" width="56" height="56" rx="12" fill="#0891B2" />
      <path d="M32 18L10 30l22 12 22-12L32 18Z" fill="white" opacity="0.3" />
      <path
        d="M32 18L10 30l22 12 22-12L32 18Z"
        stroke="white"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path
        d="M18 36v10l14 7 14-7V36"
        stroke="white"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
    </>
  ),
  book: (
    <>
      <rect x="4" y="4" width="56" height="56" rx="12" fill="#D97706" />
      <path
        d="M16 14h24a4 4 0 0 1 4 4v28a4 4 0 0 1-4 4H16a4 4 0 0 1-4-4V18a4 4 0 0 1 4-4Z"
        stroke="white"
        strokeWidth="2.5"
      />
      <path d="M20 14v36" stroke="white" strokeWidth="2.5" />
      <path
        d="M26 26h12M26 32h8"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </>
  ),
};
